import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom"; // Use useNavigate for navigation
import { toast } from "react-toastify";
import users from "../../apiConfigs/users";
import { localStorageKeys } from "../../constants";
import { useRoot } from "../../context/RootProvider";
import Button from "../../designComponents/Button";
import Input from "../../designComponents/Input";
import Typography from "../../designComponents/Typography";
import { DASHBOARD_ROUTE } from "../../routes/routePaths";
import { setAuthorizationHeader } from "../../utils/apiHelpers";
import requestAPI from "../../utils/requestAPI";
import { ContainerStyled, LoginWrapperStyled } from "./styles";
import { ReactComponent as Logo } from "../../assets/rentisity-logo.svg";
import * as Yup from "yup";

const loginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),

  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

interface LoginFormValues {
  email: string;
  password: string;
}

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuth, auth, setToken } = useRoot();
  const [passwordVisible, setPasswordVisible] = useState(false);

  const togglePasswordVisible = () => setPasswordVisible(!passwordVisible);

  const handleLogin = async (values: LoginFormValues) => {
    setIsLoading(true);
    try {
      const response = await requestAPI(
        users.login({ email: values.email, password: values.password })
      );
      const { accessToken, admin } = response;
      localStorage.setItem(localStorageKeys.accessToken, accessToken);
      localStorage.setItem(localStorageKeys.user, JSON.stringify(admin));
      setAuth(admin);
      setToken(accessToken);
      setAuthorizationHeader(accessToken);
      toast.success("Logged in successfully");
      navigate(DASHBOARD_ROUTE);
      setIsLoading(false);
    } catch (error: any) {
      toast.error(error.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (auth) {
      navigate("/");
    }
  }, [auth, navigate]);

  return (
    <LoginWrapperStyled>
      <ContainerStyled>
        <div className="header">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Logo width={150} style={{ marginBottom: "30px" }} />
            <Typography
              variant="h1"
              fontWeight="bold"
              size="largeTitle"
              color="primary"
              fontFamily="poppins"
            >
              Admin Login
            </Typography>
          </div>
          <Typography
            fontWeight="normal"
            size="subtitle"
            fontFamily="urbanist"
            color="dark.neutral_33"
          >
            Securely access your admin dashboard and manage your platform with
            confidence.
          </Typography>
        </div>

        <Formik
          initialValues={{ email: "", password: "" }}
          onSubmit={handleLogin}
          validationSchema={loginValidationSchema}
        >
          {({ values, setFieldValue, errors, touched }) => {
            return (
              <Form>
                <div className="form-container">
                  <Typography
                    style={{
                      fontSize: 12,
                      color: "#5F5F5F",
                      fontWeight: "600",
                    }}
                  >
                    Email *
                  </Typography>
                  <Input
                    placeholder="alma.lawson@example.com"
                    radius="15px"
                    name="email"
                    value={values.email}
                    style={{ borderWidth: "1px solid f1effb" }}
                    onChange={(e) => setFieldValue("email", e.target.value)}
                  />
                  {errors.email && touched.email ? (
                    <Typography color="status.red" size="subtitle">
                      {errors.email}
                    </Typography>
                  ) : (
                    ""
                  )}
                </div>
                <div className="form-container">
                  <Typography
                    style={{
                      color: "#5F5F5F",
                      fontWeight: "600",
                      fontSize: 12,
                    }}
                  >
                    Password*
                  </Typography>

                  <Input
                    placeholder="Password"
                    radius="15px"
                    name="password"
                    type={passwordVisible ? "text" : "password"}
                    style={{ borderWidth: "1px solid f1effb" }}
                    value={values.password}
                    onChange={(e) => setFieldValue("password", e.target.value)}
                  />
                  {values?.password?.length > 0 && (
                    <div
                      onClick={togglePasswordVisible}
                      style={{ cursor: "pointer" }}
                    >
                      <Typography
                        style={{
                          textAlign: "right",
                        }}
                      >
                        {!passwordVisible ? "show Password" : "hide Password"}
                      </Typography>
                    </div>
                  )}
                  {errors.password && touched.password ? (
                    <Typography color="status.red" size="subtitle">
                      {errors.password}
                    </Typography>
                  ) : (
                    ""
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Link to="/forgot-password">
                    <Typography
                      style={{
                        textAlign: "center",
                        marginTop: "20px",
                        color: "#003DA8",
                        alignSelf: "flex-end",
                        textDecoration: "underline",
                      }}
                      fontFamily="urbanist"
                      fontWeight="medium"
                      size="h5"
                    >
                      Forgot your password?
                    </Typography>
                  </Link>
                </div>
                <Button fullWidth variant="primary" isLoading={isLoading}>
                  Login
                </Button>
              </Form>
            );
          }}
        </Formik>
      </ContainerStyled>
    </LoginWrapperStyled>
  );
};

export default Login;
